/**
 * Agent Mode Types
 * 
 * Defines the two operational modes for the AI agent system:
 * - Answer Mode: Search and retrieval only (safe operations)
 * - Action Mode: Full execution capabilities (send emails, modify data)
 */

export type AgentMode = 'answer' | 'action';

export interface AgentModeConfig {
  mode: AgentMode;
  description: string;
  allowedActions: string[];
  capabilities: {
    search: boolean;
    read: boolean;
    write: boolean;
    execute: boolean;
  };
}

export const AGENT_MODE_CONFIGS: Record<AgentMode, AgentModeConfig> = {
  answer: {
    mode: 'answer',
    description: 'Search and retrieve information without making changes',
    allowedActions: [
      'email_search',
      'calendar_search',
      'contact_search',
      'contact_analyze',
      'contact_timeline',
      'task_list',
      'analysis_sentiment',
      'analysis_summary',
      'analysis_actions'
    ],
    capabilities: {
      search: true,
      read: true,
      write: false,
      execute: false
    }
  },
  action: {
    mode: 'action',
    description: 'Full capabilities including sending emails and modifying data',
    allowedActions: [
      // Search operations (from answer mode)
      'email_search',
      'calendar_search',
      'contact_search',
      'contact_analyze',
      'contact_timeline',
      'task_list',
      'analysis_sentiment',
      'analysis_summary',
      'analysis_actions',
      // Action operations (action mode only)
      'email_send',
      'email_draft',
      'email_reply',
      'email_move',
      'calendar_create',
      'calendar_update',
      'calendar_delete',
      'task_create',
      'task_update'
    ],
    capabilities: {
      search: true,
      read: true,
      write: true,
      execute: true
    }
  }
};

/**
 * Check if an action is allowed in the current mode
 */
export function isActionAllowed(action: string, mode: AgentMode): boolean {
  const config = AGENT_MODE_CONFIGS[mode];
  return config.allowedActions.includes(action);
}

/**
 * Get mode configuration
 */
export function getModeConfig(mode: AgentMode): AgentModeConfig {
  return AGENT_MODE_CONFIGS[mode];
}

/**
 * Validate mode transition
 */
export function canTransitionToMode(fromMode: AgentMode, toMode: AgentMode): boolean {
  // Allow all transitions for now
  // Future: Add logic to require confirmation for answer -> action
  return true;
}
